import React, { useEffect, useState, FC } from "react";
import classnames from "classnames";
import "../../styles/full-view-popup.scss";
import {
    Customer,
    State,
    useHasPermission,
    AppPermissions,
    Phone,
    EmailAddress,
    useLead,
    Lead,
} from "../../core";
import { useDataLoader } from "react-remote-data-hooks";
import api from "../../api";
import { UserAvatar } from "../../components/render-file/UserAvatar";
import { toastr } from "react-redux-toastr";
import { useSelector } from "react-redux";
import {
    ValidateEmail,
    ValidateNumber,
    ValidatePhoneNumberRegEx,
} from "../../utils/Validatation";
import { StatusCodes } from "http-status-codes";
import { Controller, useForm } from "react-hook-form";
import ErrorMessage from "../../components/text/ErrorMessage";
import RadioSelect from "../../components/radio-select";
import PhoneInput from "../../components/phone-input";
import noImage from "../../../src/assets/images/user-noimage.png";
import countryCodes from "../../utils/countryCodes.json"
interface FormProps {
    query: string;
}

interface AddNewLandLordPopupProps {
    onClose: () => void;
    onCreateUser: () => void;
    propertyId: string;
    AddNewUser: any;
    approvalType?: string;
    companyId?: string;
}
const ValidStages = ["fetch-customer", "create-customer", "existing-customer"];

export const AddNewLandLord: FC<AddNewLandLordPopupProps> = (
    props
) => {
    const leadState = useSelector((state: State) => state.lead);
    const [stage, setStage] = React.useState<string>("fetch-customer");
    const [customerDetails, setCustomerDetails] = React.useState<Customer>();
    const [phones, setPhones] = React.useState<Phone[] | undefined>();
    const [emails, setEmails] = React.useState<EmailAddress[] | undefined>();
    const [searchPhone, setSearchPhone] = React.useState<string>();
    const canCreateCustomer = useHasPermission(AppPermissions.CreateCustomer);

    const onAddExistingContactUser = async (data: Customer) => {
        setCustomerDetails(data);
        const updateData: any = {
            customerId: data?._id!
        };
        try {
            const response = await api.sitevisit.addNewLandLord(props?.propertyId!, updateData);
            if (response) {
                props?.onCreateUser?.();
                toastr.success("Success", "User added successfully");
                props?.onClose?.()
            }
        } catch (error) {
            toastr.error("Error", error?.response?.data?.message || "Error while adding contact");
            // setIsSubmiting(false);
        }
    };

    const onFetchDetails = (
        data: Customer,
        query: string,
        searchType?: string
    ) => {
        setCustomerDetails(data);
        setPhones(undefined);
        setEmails(undefined);
        setSearchPhone("");
        if (searchType === "mobile" && !data && ValidateNumber(query)) {
            setPhones([
                { phone: query, phoneType: "Mobile", isPrimary: true },
            ]);
        }
        if (searchType === "email" && !data && ValidateEmail(query)) {
            setEmails([{ email: query, emailType: "Business", isPrimary: true }]);
        }

        if (data) {
            setPhones(data?.phones);
            setEmails(data?.emails);
        }
        if (data && ValidatePhoneNumberRegEx(query) && searchType === "mobile") {
            setSearchPhone(query);
        }
        setStage(
            data || !canCreateCustomer ? "existing-customer" : "create-customer"
        );
    };

    useEffect(() => {
        if (stage && !ValidStages.find((s) => s === stage)) {
            setStage("fetch-customer"); // default stage
        }
    }, [stage]);

    return (
        <>
            <div className="row no-gutters" >
                < div className="col-12 member-detail bg-white zIndex-1 top-0 left-0 h-100 " >
                    {
                        stage === "fetch-customer" && (
                            <div className="">
                                <SearchCustomer
                                    onFetchDetails={(
                                        details: Customer,
                                        query: string,
                                        searchType: string
                                    ) => onFetchDetails(details, query, searchType)}
                                    setStage={(stageName: string) => setStage(stageName)}
                                ></SearchCustomer>
                            </div>
                        )
                    }
                    {
                        stage === "existing-customer" && (
                            <>
                                <ExistingClient
                                    customerDetails={customerDetails}
                                    setStage={(stageName: string) => setStage(stageName)}
                                    phone={searchPhone}
                                    onLeadClick={() => undefined}
                                    addExistingCustomerAsUser={(details: Customer) =>
                                        onAddExistingContactUser(details)
                                    }
                                    isCompanyLead={true}
                                ></ExistingClient>
                            </>
                        )
                    }
                    {
                        stage === "create-customer" && (
                            <>
                                <props.AddNewUser
                                    onClose={() => props?.onClose?.()}
                                    onCreateUser={props?.onCreateUser}
                                    propertyId={props?.propertyId}
                                    phones={phones}
                                    emails={emails}
                                    approvalType={props?.approvalType}
                                    companyId={props?.companyId}
                                />
                            </>
                        )
                    }
                </div >
            </div >
        </>
    );
};


const SearchCustomer: React.FunctionComponent<{
    error?: string;
    onFetchDetails: (data: Customer, query: string, searchType: string) => void;
    setStage: (stage: string) => void;
    onCloseSetStage?: () => void;
    onCancel?: () => void;
    hasContact?: boolean;
}> = (props) => {
    const wrapperRef = React.useRef<HTMLInputElement>(null);
    const [searchType, setSearchType] = useState<string>("mobile");
    const [isFetching, setFetching] = useState<boolean>(false);
    const { control, handleSubmit, errors, watch, setValue } = useForm<FormProps>(
        {
            reValidateMode: "onChange",
            mode: "onSubmit",
        }
    );

    const onSubmit = async (data: { query: string }) => {
        setFetching(true);
        const response = await api.customer.findCustomer(
            data.query,
            searchType === "civilId" ? "civilId" : "mobile"
        );
        props.onFetchDetails(response, data.query, searchType);
        setFetching(false);
    };

    const fieldName =
        searchType === "mobile"
            ? "Mobile number"
            : searchType === "email"
                ? "Email address"
                : "";

    const resetSearchQuery = (type: string) => {
        if (type === "mobile") {
            setValue("query", "91");
        } else {
            setValue("query", "");
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="p-4">
                <div className="form-group font-weight-semibold mb-4">
                    <div className="mb-2 text-uppercase search-lead">
                        <RadioSelect
                            defaultValue={searchType}
                            options={[
                                { label: "Mobile number", value: "mobile" },
                                { label: "Email address", value: "email" },
                            ]}
                            optionLabel={"label"}
                            optionValue={"value"}
                            onChange={(val) => {
                                setSearchType(val?.toString());
                                resetSearchQuery(val?.toString());

                                if (wrapperRef && wrapperRef.current) {
                                    wrapperRef?.current?.focus?.();
                                }
                            }}
                            paddingClass="pr-0"
                        />
                    </div>
                    {searchType === "mobile" && (
                        <Controller
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: 'Mobile number is required'
                                },
                                validate: (value) => {
                                    if (countryCodes?.some(item => item?.dial_code === `+${value}`)) {
                                        return "Mobile number is required"
                                    } else {
                                        const hasError = ValidatePhoneNumberRegEx(value.trim());
                                        if (!hasError) return "Invalid Mobile number"
                                    }
                                },
                            }}
                            name="query"
                            defaultValue="91"
                            render={({ onChange, value, name }) => (
                                <PhoneInput
                                    placeholder="MOBILE NUMBER"
                                    value={value}
                                    onChange={onChange}
                                    inputProps={{ autoFocus: true }}
                                />
                            )}
                        />
                    )}

                    {searchType === "email" && (
                        <Controller
                            control={control}
                            rules={{
                                required: true,
                                validate: (value) => {
                                    return ValidateEmail(value.trim());
                                },
                            }}
                            name="query"
                            render={({ onChange, value, name }) => (
                                <input
                                    type="text"
                                    className="form-control rounded-sm font-weight-semibold"
                                    placeholder="EMAIL ADDRESS"
                                    name={name}
                                    value={value}
                                    id="query"
                                    onChange={onChange}
                                    autoFocus
                                    ref={wrapperRef}
                                />
                            )}
                        />
                    )}

                    {errors.query && errors?.query?.message && searchType === "mobile" && (
                        <ErrorMessage>{errors.query.message}</ErrorMessage>
                    )}

                    {errors.query && errors.query.type === "required" && searchType !== "mobile" && (
                        <ErrorMessage>{fieldName} is required</ErrorMessage>
                    )}

                    {errors.query && errors.query.type === "validate" && searchType !== "mobile" && (
                        <ErrorMessage>Invalid {fieldName}</ErrorMessage>
                    )}

                </div>
                <div className="mb-2 text-right">
                    {props.hasContact && (
                        <button
                            className="btn btn-outline-dark font-normal font-weight-bold mr-3"
                            disabled={!watch("query") || isFetching}
                            type="button"
                            onClick={props.onCancel}
                        >
                            Cancel
                        </button>
                    )}
                    <button
                        className="btn btn-primary font-normal font-weight-bold"
                        disabled={!watch("query") || isFetching}
                    >
                        Next
                    </button>
                </div>
            </div>
        </form>
    );
};

const ExistingClient: React.FunctionComponent<{
    customerDetails?: Customer;
    setStage: (stage: string) => void;
    phone?: string;
    onLeadClick: (id: string) => void;
    civilId?: string;
    isCompanyLead?: boolean;
    addExistingCustomerAsUser?: (data: Customer) => void;
    onClose?: () => void;
}> = (props) => {

    return (
        <>
            {props.customerDetails && (
                <div className="d-flex border-bottom align-items-center py-2 px-4 ">
                    <h6 className="mb-0">It looks this client already exists</h6>
                </div>
            )}
            <div className="p-4 ">
                <p className="font-sm mb-2">
                    {props.customerDetails ? (
                        <>
                            {!props.customerDetails.isCompanyCustomer ? (
                                <>
                                    {" "}
                                    We ‘ve found an existing client with given phone number in
                                    your records{" "}
                                </>
                            ) : (
                                <>
                                    {" "}
                                    We ‘ve found an existing client with given phone number in
                                    your records
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            We haven't found an existing client with given phone number in
                            your records
                        </>
                    )}
                </p>
                {props.customerDetails && (
                    <div className="border rounded p-3 d-flex mb-4">
                        <div className="avatar position-relative mr-3">
                            <img
                                src={props.customerDetails?.photo?.location || noImage}
                                className="img-cover img-fluid rounded-circle"
                                alt="user image"
                            />
                        </div>
                        <div>
                            <h6 className="font-weight-semibold mb-1">
                                {props.customerDetails?.firstName}{" "}
                                {props.customerDetails?.lastName}
                            </h6>
                            {props.customerDetails &&
                                props.customerDetails.phones &&
                                props.customerDetails.phones.length > 0 && (
                                    <p className="font-sm font-weight-semibold mb-1">
                                        <a
                                            href={`tel:+${props.customerDetails.phones[0].phone}`}
                                            className="text-gray-700"
                                        >
                                            +{props.customerDetails.phones[0].phone}
                                        </a>
                                    </p>
                                )}

                            {props.customerDetails &&
                                props.customerDetails.emails &&
                                props.customerDetails.emails.length > 0 && (
                                    <p className="font-sm font-weight-semibold mb-1 text-gray-700">
                                        <a
                                            href="mailto:harold_adams@gmail.com"
                                            className="text-gray-700"
                                        >
                                            {props.customerDetails.emails[0].email}
                                        </a>
                                    </p>
                                )}
                        </div>
                    </div>
                )}
                <h6 className="pt-2 mb-3 font-weight-normal font-sm">
                    Here's what you can do:
                </h6>
                <button
                    onClick={() => {
                        props.addExistingCustomerAsUser?.(props.customerDetails!);
                    }}
                    className="btn btn-primary btn-block font-normal font-weight-bold"
                >
                    Add Contact
                </button>
                <button
                    onClick={() =>
                        props.onClose ? props.onClose?.() : props.setStage("add-user")
                    }
                    className="btn btn-outline-dark btn-block font-normal font-weight-bold btn-goback text-gray-500 mb-2"
                >
                    Go Back
                </button>
            </div>
        </>
    );
};

export { SearchCustomer, ExistingClient };

